// ============================================================
//  CUSTOMER DETAIL PAGE — ADMIN DASHBOARD
//  Path: src/pages/CustomerDetailPage.tsx
// ============================================================

import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { getUserById, listTasksForCustomer, getCustomerPaymentHistory, getTaskDetail, getSessionNotesForAdmin, dismissUserIssue, restoreUserIssue, listUserIssueDismissals, getCustomerTimeline } from '../services/admin.service'
import { TimelineSection } from '../components/ActivityTimeline'
import SupportMessageButton from '../components/SupportMessageButton'
// 2026-10-10: open help requests from this customer, under their name.
import SupportRequestsBadge from '../components/SupportRequestsBadge'
import { Task, CustomerPaymentHistory, SessionNote, DismissedIssue } from '../types'

interface UserIssue {
  code:     string
  severity: 'blocking' | 'warning'
  message:  string
}

// Computed server-side (admin.service.js's buildCustomerNeedsAction) —
// deliberately separate from UserIssue: nothing is wrong, an admin
// decision is just outstanding. Always [] today (no customer approval
// workflow exists in the schema) — kept for parity with the Expert
// detail page, which is where this will actually show real items.
interface NeedsActionItem {
  code:    string
  message: string
}

interface CustomerUser {
  id: string; firstName: string; lastName: string; phone: string | null; email: string | null
  isActive: boolean; createdAt: string
  customer: {
    id: string; homeAddress: string | null; workAddress: string | null; city: string | null; country: string | null
    // Map points saved when the address was looked up (null = not found / not looked up).
    latitude?: number | null; longitude?: number | null
    workLatitude?: number | null; workLongitude?: number | null
  } | null
  // Computed server-side (see admin.service.js's buildCustomerIssues)
  // — deactivated account, a failed charge, an open dispute.
  issues?: UserIssue[]
  // See NeedsActionItem above.
  needsAction?: NeedsActionItem[]
}

type Tab = 'profile' | 'tasks' | 'payments' | 'activity'

const TASK_STATUS_COLORS: Record<string, string> = {
  open: 'bg-blue-600/20 text-blue-400', assigned: 'bg-yellow-600/20 text-yellow-400',
  pending_completion: 'bg-orange-600/20 text-orange-400', completed: 'bg-green-600/20 text-green-400',
  disputed: 'bg-red-600/20 text-red-400', cancelled: 'bg-gray-600/20 text-gray-400',
}

// authorized = the card is only HELD (not charged yet); capturing = being
// charged; releasing = the hold is being released.
const PAYMENT_STATUS_LABELS: Record<string, string> = {
  authorized: 'on hold', capturing: 'charging', releasing: 'releasing hold',
}
const isHeld = (s: string | null | undefined) => s === 'authorized' || s === 'releasing'

const PAYMENT_STATUS_COLORS: Record<string, string> = {
  authorized: 'bg-blue-600/20 text-blue-400', capturing: 'bg-yellow-600/20 text-yellow-400', releasing: 'bg-yellow-600/20 text-yellow-400',
  pending: 'bg-gray-600/20 text-gray-400', paid: 'bg-green-600/20 text-green-400',
  refunded: 'bg-red-600/20 text-red-400', partially_refunded: 'bg-orange-600/20 text-orange-400', failed: 'bg-red-600/20 text-red-400',
  refunding: 'bg-yellow-600/20 text-yellow-400',
}

const CustomerDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [customer, setCustomer]   = useState<CustomerUser | null>(null)
  const [tab, setTab]             = useState<Tab>('profile')
  const [tasks, setTasks]         = useState<Task[]>([])
  const [tasksLoaded, setTasksLoaded] = useState(false)
  const [payments, setPayments]   = useState<CustomerPaymentHistory | null>(null)
  const [paymentsLoaded, setPaymentsLoaded] = useState(false)
  const [selectedTask, setSelectedTask] = useState<any>(null)
    const [previewPhoto, setPreviewPhoto] = useState<{ fileUrl: string; fileName: string } | null>(null)
    const [sessionNotes, setSessionNotes] = useState<SessionNote[]>([])
  const [loading, setLoading]     = useState(true)
  // FIX: load failures on the two tabs and the task panel were
  // console-only. Payment History then showed "Loading..." forever and
  // Posted Tasks showed "No tasks posted yet" — which read as real data.
  // reloadKey re-runs the tab loader when "Try again" is clicked.
  const [tasksError, setTasksError]       = useState('')
  const [paymentsError, setPaymentsError] = useState('')
  const [taskDetailError, setTaskDetailError] = useState('')
  const [reloadKey, setReloadKey]         = useState(0)

  const loadErrorMessage = (err: any, what: string) =>
    err?.response?.data?.message || (err?.isNetworkError
      ? 'Could not reach the server — check your connection and try again.'
      : `Could not load ${what} — try again.`)

  const [dismissingIssue, setDismissingIssue] = useState<string | null>(null)
  const [issueActionError, setIssueActionError] = useState('')
  const [showDismissed, setShowDismissed] = useState(false)
  const [dismissedIssues, setDismissedIssues] = useState<DismissedIssue[] | null>(null)
  const [dismissedLoading, setDismissedLoading] = useState(false)
  const [restoringIssue, setRestoringIssue] = useState<string | null>(null)

  // FIX (production-readiness pass): this used to pull up to 1000
  // customers via listUsers and find() the right one client-side —
  // the exact same anti-pattern admin.api.ts's own comment already
  // documents fixing for ExpertDetailPage.tsx ("previously pulled
  // the whole list and filtered locally, which quietly stopped
  // working past the first 100 experts — the backend clamps limit
  // to 100"), just never applied here. A customer past whatever
  // limit the backend clamps to would silently show "Customer not
  // found" even though they exist. getUserById fetches the one
  // record directly and doesn't have that failure mode.
  //
  // Pulled out of the effect (not just inline) so the Issues panel's
  // "Clear" button can re-run it after a dismiss, without duplicating
  // the fetch logic.
  const fetchCustomer = async () => {
    if (!id) { setLoading(false); return }
    try {
      const res = await getUserById(id)
      setCustomer(res.data.data)
    } catch (err) {
      console.error('Failed to fetch customer:', (err as any)?.response?.status, (err as any)?.message)
      setCustomer(null)
    } finally {
      setLoading(false)
    }
  }

  // fetchCustomer is redefined every render (it closes over state setters,
  // not props), so listing it as a dependency would re-run this effect on
  // every render instead of only when the route's :id changes, which is
  // the intended behavior.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchCustomer() }, [id])

  // Fixing the underlying problem clears an issue automatically — this
  // is only for "I've looked at this, stop showing it to me". It stays
  // cleared unless a NEW occurrence of the same issue happens (see
  // admin.service.js's IssueDismissal comment).
  const handleDismissIssue = async (issueCode: string) => {
    if (!id) return
    setDismissingIssue(issueCode)
    setIssueActionError('')
    try {
      await dismissUserIssue(id, issueCode)
      await fetchCustomer()
    } catch (err: any) {
      console.error('Failed to dismiss issue:', (err as any)?.response?.status, (err as any)?.message)
      setIssueActionError(err?.response?.data?.message || 'Could not clear this issue — try again.')
    } finally {
      setDismissingIssue(null)
    }
  }

  const loadDismissedIssues = async () => {
    if (!id) return
    setDismissedLoading(true)
    try {
      const res = await listUserIssueDismissals(id)
      setDismissedIssues(res.data.data)
    } catch (err: any) {
      console.error('Failed to fetch dismissed issues:', (err as any)?.response?.status, (err as any)?.message)
      setIssueActionError(err?.response?.data?.message || 'Could not load dismissed issues — try again.')
    } finally {
      setDismissedLoading(false)
    }
  }

  const toggleShowDismissed = () => {
    const next = !showDismissed
    setShowDismissed(next)
    if (next && dismissedIssues === null) loadDismissedIssues()
  }

  const handleRestoreIssue = async (issueCode: string) => {
    if (!id) return
    setRestoringIssue(issueCode)
    setIssueActionError('')
    try {
      await restoreUserIssue(id, issueCode)
      await Promise.all([fetchCustomer(), loadDismissedIssues()])
    } catch (err: any) {
      console.error('Failed to restore issue:', (err as any)?.response?.status, (err as any)?.message)
      setIssueActionError(err?.response?.data?.message || 'Could not restore this issue — try again.')
    } finally {
      setRestoringIssue(null)
    }
  }

  useEffect(() => {
    if (!customer?.customer?.id) return
    const customerId = customer.customer.id
    if (tab === 'tasks' && !tasksLoaded) {
      setTasksError('')
      listTasksForCustomer(customerId).then((res) => { setTasks(res.data.data); setTasksLoaded(true) })
        .catch((err) => {
          console.error('Failed to fetch customer tasks:', (err as any)?.response?.status, (err as any)?.message)
          setTasksError(loadErrorMessage(err, 'this customer’s tasks'))
        })
    }
    if (tab === 'payments' && !paymentsLoaded) {
      setPaymentsError('')
      // An older backend sent a plain task list with no totals — accept
      // both shapes so the tab can never crash on the response again.
      getCustomerPaymentHistory(customerId).then((res) => {
        const d = res?.data?.data
        setPayments(Array.isArray(d)
          ? { totalSpent: null, summary: null, tasks: d }
          : { totalSpent: d?.totalSpent ?? null, summary: d?.summary ?? null, tasks: Array.isArray(d?.tasks) ? d.tasks : [], limit: d?.limit })
        setPaymentsLoaded(true)
      })
        .catch((err) => {
          console.error('Failed to fetch payment history:', (err as any)?.response?.status, (err as any)?.message)
          setPaymentsError(loadErrorMessage(err, 'payment history'))
        })
    }
  }, [tab, customer, tasksLoaded, paymentsLoaded, reloadKey])

  const openTaskDetail = async (taskId: string) => {
    setSessionNotes([])
    setTaskDetailError('')
    try {
      const res = await getTaskDetail(taskId)
      setSelectedTask(res.data.data)

      const relevantApp = res.data.data.applications?.find(
        (a: any) => ['hired', 'completed', 'shortlisted'].includes(a.status)
      )
      if (relevantApp) {
        try {
          const notesRes = await getSessionNotesForAdmin(relevantApp.id)
          setSessionNotes(notesRes.data.data.notes)
        } catch (notesErr) {
          console.error('Failed to fetch session notes:', (notesErr as any)?.response?.status, (notesErr as any)?.message)
        }
      }
    } catch (err) {
      console.error('Failed to fetch task detail:', (err as any)?.response?.status, (err as any)?.message)
      setTaskDetailError(loadErrorMessage(err, 'this task'))
    }
  }

  const LoadError: React.FC<{ message: string }> = ({ message }) => (
    <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 flex items-center justify-between gap-3">
      <p className="text-red-400 text-sm">{message}</p>
      <button onClick={() => setReloadKey((k) => k + 1)}
        className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg hover:bg-border-light transition-colors shrink-0">
        Try again
      </button>
    </div>
  )

  const formatDate = (d: string | null | undefined) =>
    d ? new Date(d).toLocaleString('en-CA', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'

  if (loading) return <div className="flex items-center justify-center h-64"><div className="text-gray-400">Loading...</div></div>
  if (!customer) return <div className="text-gray-400">Customer not found</div>

  return (
    <div>
      <button onClick={() => navigate('/customers')} className="text-sm text-brand-400 hover:text-brand-300 mb-4">← Back to Customers</button>

      <div className="flex items-center gap-4 mb-6">
        <div className="w-14 h-14 rounded-full bg-green-700 flex items-center justify-center text-white text-xl font-bold">
          {customer.firstName?.[0]?.toUpperCase() || '?'}
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">{customer.firstName} {customer.lastName}</h1>
          <p className="text-gray-400 text-sm">{customer.email || customer.phone}</p>
          <SupportRequestsBadge userId={customer.id} appType="customer" />
          <SupportMessageButton userId={customer.id} name={`${customer.firstName || ''} ${customer.lastName || ''}`.trim()} appType="customer" />
        </div>
      </div>

      {/* Needs Action banner — a decision an admin owes this account
          right now. Deliberately separate from, and shown above, the
          Issues panel below: nothing here is wrong, so there's no
          "Clear" button — it's computed live (see admin.service.js's
          buildCustomerNeedsAction) and disappears on its own once the
          decision is made. Always empty today (no customer approval
          workflow exists in the schema) — kept for parity with the
          Expert detail page, which is where this actually shows items. */}
      {customer.needsAction && customer.needsAction.length > 0 && (
        <div className="mb-4 space-y-2">
          {customer.needsAction.map((item) => (
            <div key={item.code}
              className="rounded-xl p-3 text-sm flex items-start gap-2 bg-brand-500/10 border border-brand-500/30 text-brand-300">
              <span>🔔</span>
              <span>{item.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Issues panel — every blocking or warning condition currently
          on this account, in one place, regardless of which tab is
          open. See admin.service.js's buildCustomerIssues for what's
          checked (deactivated account, failed charge, open dispute). */}
      {issueActionError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 mb-2 text-red-400 text-sm flex items-center justify-between gap-3">
          {issueActionError}
          <button onClick={() => setIssueActionError('')} className="text-red-300 hover:text-white shrink-0">✕</button>
        </div>
      )}
      {customer.issues && customer.issues.length > 0 && (
        <div className="mb-6 space-y-2">
          {customer.issues.map((issue) => (
            <div key={issue.code}
              className={`rounded-xl p-3 text-sm flex items-start justify-between gap-3 ${
                issue.severity === 'blocking'
                  ? 'bg-red-500/10 border border-red-500/30 text-red-400'
                  : 'bg-yellow-500/10 border border-yellow-500/30 text-yellow-400'
              }`}>
              <span className="flex items-start gap-2">
                <span>{issue.severity === 'blocking' ? '⛔' : '⚠️'}</span>
                <span>{issue.message}</span>
              </span>
              <button
                onClick={() => handleDismissIssue(issue.code)}
                disabled={dismissingIssue === issue.code}
                title="Mark as handled — reappears automatically if this happens again"
                className="text-xs px-2 py-1 rounded-lg bg-black/20 hover:bg-black/30 disabled:opacity-50 transition-colors shrink-0 whitespace-nowrap">
                {dismissingIssue === issue.code ? 'Clearing…' : 'Clear'}
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mb-6">
        <button
          onClick={toggleShowDismissed}
          className="text-xs text-gray-400 hover:text-white transition-colors underline decoration-dotted underline-offset-2">
          {showDismissed ? 'Hide dismissed issues' : 'Show dismissed issues'}
        </button>
        {showDismissed && (
          <div className="mt-2 space-y-2">
            {dismissedLoading && (
              <div className="text-sm text-gray-500">Loading dismissed issues…</div>
            )}
            {!dismissedLoading && dismissedIssues && dismissedIssues.length === 0 && (
              <div className="text-sm text-gray-500">No dismissed issues.</div>
            )}
            {!dismissedLoading && dismissedIssues && dismissedIssues.map((d) => (
              <div key={d.code}
                className="rounded-xl p-3 text-sm flex items-start justify-between gap-3 bg-surface-tertiary border border-border-light text-gray-300">
                <span className="flex flex-col gap-1">
                  <span className="flex items-center gap-2">
                    <span>{d.label}</span>
                    {d.newOccurrenceSince && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/10 border border-yellow-500/30 text-yellow-400">
                        New occurrence since dismissal
                      </span>
                    )}
                    {d.stillSuppressing && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-black/20 text-gray-400">
                        Currently hidden
                      </span>
                    )}
                  </span>
                  <span className="text-xs text-gray-500">
                    Dismissed {new Date(d.dismissedAt).toLocaleString()}
                  </span>
                </span>
                <button
                  onClick={() => handleRestoreIssue(d.code)}
                  disabled={restoringIssue === d.code}
                  title="Bring this issue back so it shows in the panel above"
                  className="text-xs px-2 py-1 rounded-lg bg-black/20 hover:bg-black/30 disabled:opacity-50 transition-colors shrink-0 whitespace-nowrap">
                  {restoringIssue === d.code ? 'Restoring…' : 'Restore'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-2 mb-6">
        {(['profile', 'tasks', 'payments', 'activity'] as Tab[]).map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium capitalize rounded-xl transition-colors ${
              tab === t ? 'bg-brand-600 text-white' : 'bg-surface-tertiary text-gray-400 hover:text-white'
            }`}>{t === 'tasks' ? 'Posted Tasks' : t === 'payments' ? 'Payment History' : t === 'activity' ? 'Activity Timeline' : t}</button>
        ))}
      </div>

      {/* Every recorded action across all of this customer's tasks —
          what happened, when, who did it, and for arrivals / check-ins
          where (distance to the address, verified, map). Clicking a
          task name opens it in Posted Tasks. */}
      {/* customerRecordId is read into a plain string first — TypeScript
          does not carry the null check into the fetcher's arrow function
          (TS18047 'customer.customer' is possibly 'null'). */}
      {tab === 'activity' && (() => {
        const customerRecordId = customer?.customer?.id
        if (!customerRecordId) return null
        return (
          <div className="bg-surface-card border border-border-light rounded-2xl p-6">
            <TimelineSection
              title="Activity across all tasks (newest first)"
              fetcher={() => getCustomerTimeline(customerRecordId)}
              showTask
              onOpenTask={(taskId) => { setTab('tasks'); openTaskDetail(taskId) }}
              reloadKey={customerRecordId}
            />
          </div>
        )
      })()}

      {tab === 'profile' && (
        <div className="bg-surface-card border border-border-light rounded-2xl p-6 max-w-xl space-y-3">
          <DetailRow label="Status" value={customer.isActive ? 'Active' : 'Inactive'} />
          <DetailRow label="Phone" value={customer.phone || '—'} />
          <DetailRow label="Email" value={customer.email || '—'} />
          <DetailRow label="Home Address" value={customer.customer?.homeAddress || '—'} note={mapNote(customer.customer?.homeAddress, customer.customer?.latitude, customer.customer?.longitude)} />
          <DetailRow label="Work Address" value={customer.customer?.workAddress || '—'} note={mapNote(customer.customer?.workAddress, customer.customer?.workLatitude, customer.customer?.workLongitude)} />
          <DetailRow label="City" value={customer.customer?.city || '—'} />
          <DetailRow label="Country" value={customer.customer?.country || '—'} />
          <DetailRow label="Joined" value={new Date(customer.createdAt).toLocaleDateString()} />
        </div>
      )}

      {tab === 'tasks' && tasksError && <LoadError message={tasksError} />}
      {tab === 'tasks' && taskDetailError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-red-400 text-sm">
          {taskDetailError}
          <button onClick={() => setTaskDetailError('')} className="ml-3 text-red-300 hover:text-white">✕</button>
        </div>
      )}
      {tab === 'tasks' && !tasksError && (
        <div className="flex gap-6">
          <div className={`bg-surface-card border border-border-light rounded-2xl overflow-hidden ${selectedTask ? 'flex-1' : 'w-full'}`}>
            {!tasksLoaded ? (
              <p className="text-center py-8 text-gray-400">Loading tasks...</p>
            ) : tasks.length === 0 ? (
              <p className="text-center py-8 text-gray-400">No tasks posted yet</p>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border-light">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Task</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Expert</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Status</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Charged</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((t: any) => {
                    const hired = t.applications?.find((a: any) => a.status === 'hired' || a.status === 'completed')
                    return (
                      <tr key={t.id} onClick={() => openTaskDetail(t.id)}
                        className="border-b border-border-light cursor-pointer hover:bg-surface-tertiary/50 transition-colors">
                        <td className="px-5 py-4">
                          <p className="text-sm font-medium text-white">{t.title}</p>
                          <p className="text-xs text-gray-500">{t.category?.name}</p>
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-300">
                          {hired ? `${hired.expert?.user.firstName} ${hired.expert?.user.lastName}` : <span className="text-gray-600">Not hired</span>}
                        </td>
                        <td className="px-5 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${TASK_STATUS_COLORS[t.status] || 'bg-gray-600/20 text-gray-400'}`}>{t.status.replace('_', ' ')}</span>
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-300">
                          {t.totalCharged != null ? `$${t.totalCharged.toFixed(2)}` : '—'}
                          {t.totalCharged != null && isHeld(t.paymentStatus) && <span className="block text-xs text-blue-400">on hold</span>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>

          {selectedTask && (
            <div className="w-96 bg-surface-card border border-border-light rounded-2xl p-5 h-fit sticky top-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Task Details</h3>
                <button onClick={() => setSelectedTask(null)} className="text-gray-500 hover:text-white text-lg">✕</button>
              </div>
              <p className="text-base font-semibold text-white mb-1">{selectedTask.title}</p>
              <p className="text-xs text-gray-500 mb-4">{selectedTask.category?.name} · {selectedTask.urgencyLevel?.name}</p>

              <p className="text-xs text-gray-500 uppercase mb-2">Location</p>
              <DetailRow label="Area" value={selectedTask.area || '—'} />
              <DetailRow label="Address" value={selectedTask.address || '—'} />

              <p className="text-xs text-gray-500 uppercase mb-2 mt-4">Payment — every deduction that raised the price</p>
              <DetailRow label="Base Price" value={selectedTask.finalPrice != null ? `$${selectedTask.finalPrice.toFixed(2)}` : '—'} />
              <DetailRow label="Insurance" value={selectedTask.insuranceFee != null ? `$${selectedTask.insuranceFee.toFixed(2)}` : '—'} />
              {/* FIX: Service Fee and Processing Fee are charged to the customer
                  by default (100% customer / 0% tasker split unless a category
                  overrides it — see resolveServiceFee / resolveProcessingFee in
                  payment.service.js) but were missing here entirely, so this
                  panel understated exactly what made the customer pay more. */}
              <DetailRow label="Service Fee" value={(selectedTask as any).serviceFee != null ? `$${(selectedTask as any).serviceFee.toFixed(2)}` : '—'} />
              <DetailRow label="Processing Fee" value={(selectedTask as any).processingFee != null ? `$${(selectedTask as any).processingFee.toFixed(2)}` : '—'} />
              <DetailRow label="Tax" value={selectedTask.taxAmount != null ? `$${selectedTask.taxAmount.toFixed(2)}` : '—'} />
              <DetailRow label={isHeld(selectedTask.paymentStatus) ? 'Total On Hold' : 'Total Charged'} value={selectedTask.totalCharged != null ? `$${selectedTask.totalCharged.toFixed(2)}` : '—'} />
              {selectedTask.paymentStatus && (
                <DetailRow label="Payment Status" value={PAYMENT_STATUS_LABELS[selectedTask.paymentStatus] || String(selectedTask.paymentStatus).replace(/_/g, ' ')} />
              )}

              {sessionNotes.length > 0 && (
                <>
                  <p className="text-xs text-gray-500 uppercase mb-2 mt-4">Session Notes</p>
                  <div className="space-y-2 mb-2">
                    {sessionNotes.map((note) => (
                      <div key={note.id} className="bg-surface-tertiary border border-border-light rounded-lg px-3 py-2">
                        <p className="text-xs text-gray-500 mb-1">
                          {new Date(note.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                        <p className="text-xs text-gray-300 whitespace-pre-wrap">{note.content}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}

                       <p className="text-xs text-gray-500 uppercase mb-2 mt-4">Timing</p>
              <DetailRow label="Posted" value={formatDate(selectedTask.createdAt)} />
              <DetailRow label="Hired" value={formatDate(selectedTask.hiredAt)} />

                      {selectedTask.attachments && selectedTask.attachments.length > 0 && (
                <>
                  <p className="text-xs text-gray-500 uppercase mb-2 mt-4">Photos</p>
                  {['task_photo', 'completion_photo'].map((type) => {
                    const photos = selectedTask.attachments.filter((a: any) => a.type === type)
                    if (photos.length === 0) return null
                    return (
                      <div key={type} className="mb-3">
                        <p className="text-xs text-gray-500 mb-1.5">{type === 'task_photo' ? 'From customer' : 'From tasker (completion)'}</p>
                        <div className="flex gap-2 flex-wrap">
                          {photos.map((photo: any) => (
                            <button key={photo.id} onClick={() => setPreviewPhoto(photo)}
                              className="w-16 h-16 rounded-lg overflow-hidden border border-border-light hover:border-brand-500 transition-colors">
                              <img src={photo.fileUrl} alt={photo.fileName} className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </>
              )}
            </div>
          )}
        </div>
      )}

            {tab === 'payments' && (
        <div>
          {paymentsError ? (
            <LoadError message={paymentsError} />
          ) : !payments ? (
            <p className="text-gray-400">Loading payment history...</p>
          ) : (
            <>
              <Link to={`/reports?scope=customer&id=${customer.customer?.id}`} className="inline-block mb-4 text-sm text-brand-400 hover:text-brand-300">
                📈 View Detailed Report (daily/weekly/monthly/yearly) →
              </Link>
              {/* FIX (blank page): this read payments.totalSpent.toFixed(2)
                  while the backend sent no totalSpent — the crash blanked
                  the page. All totals now come from the server; a missing
                  one shows "—" instead of crashing. */}
              <div className="flex flex-wrap gap-4 mb-6">
                <div className="bg-surface-card border border-border-light rounded-2xl p-5 min-w-[12rem]">
                  <p className="text-2xl font-bold text-white">{payments.totalSpent != null ? `$${payments.totalSpent.toFixed(2)}` : '—'}</p>
                  <p className="text-sm text-gray-400 mt-1">Total Spent</p>
                  <p className="text-xs text-gray-600 mt-0.5">What the customer paid, after refunds</p>
                </div>
                {payments.summary && (
                  <>
                    <div className="bg-surface-card border border-border-light rounded-2xl p-5 min-w-[12rem]">
                      <p className="text-2xl font-bold text-white">${payments.summary.totalCharged.toFixed(2)}</p>
                      <p className="text-sm text-gray-400 mt-1">Total Charged</p>
                    </div>
                    <div className="bg-surface-card border border-border-light rounded-2xl p-5 min-w-[12rem]">
                      <p className="text-2xl font-bold text-green-400">${payments.summary.totalRefunded.toFixed(2)}</p>
                      <p className="text-sm text-gray-400 mt-1">Total Refunded</p>
                    </div>
                    {/* Held on the card for upcoming jobs — not charged yet, and
                        not counted in Total Spent / Total Charged. */}
                    {payments.summary.totalOnHold != null && (
                      <div className="bg-surface-card border border-border-light rounded-2xl p-5 min-w-[12rem]">
                        <p className="text-2xl font-bold text-blue-400">${payments.summary.totalOnHold.toFixed(2)}</p>
                        <p className="text-sm text-gray-400 mt-1">On Hold</p>
                        <p className="text-xs text-gray-600 mt-0.5">Held on the card, not charged yet</p>
                      </div>
                    )}
                  </>
                )}
              </div>
              {payments.limit != null && payments.tasks.length >= payments.limit && (
                <p className="text-xs text-gray-500 mb-2">Showing the newest {payments.limit} tasks — the totals above include all of them.</p>
              )}

              <div className="bg-surface-card border border-border-light rounded-2xl overflow-hidden">
                {payments.tasks.length === 0 ? (
                  <p className="text-center py-8 text-gray-400">No transactions yet</p>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border-light">
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Task</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Expert</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Base Price</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Insurance</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Service Fee</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Processing Fee</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Tax</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Total Charged</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Refunded</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Paid (net)</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Status</th>
                        <th className="text-left px-5 py-3 text-xs font-semibold text-gray-400 uppercase">Hired</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.tasks.map((t: any) => (
                        <tr key={t.id} className="border-b border-border-light">
                          <td className="px-5 py-4 text-sm text-white">{t.title}</td>
                          <td className="px-5 py-4 text-sm text-gray-300">{t.hiredExpertName || <span className="text-gray-600">Not hired</span>}</td>
                          <td className="px-5 py-4 text-sm text-gray-300">{t.finalPrice != null ? `$${t.finalPrice.toFixed(2)}` : '—'}</td>
                          <td className="px-5 py-4 text-sm text-gray-300">{t.insuranceFee ? `$${t.insuranceFee.toFixed(2)}` : '—'}</td>
                          <td className="px-5 py-4 text-sm text-gray-300">{t.serviceFee ? `$${t.serviceFee.toFixed(2)}` : '—'}</td>
                          <td className="px-5 py-4 text-sm text-gray-300">{t.processingFee ? `$${t.processingFee.toFixed(2)}` : '—'}</td>
                          <td className="px-5 py-4 text-sm text-gray-300">{t.taxAmount ? `$${t.taxAmount.toFixed(2)}` : '—'}</td>
                          <td className="px-5 py-4 text-sm text-white font-medium">
                            {t.totalCharged != null ? `$${t.totalCharged.toFixed(2)}` : '—'}
                            {t.totalCharged != null && isHeld(t.paymentStatus) && <span className="block text-xs text-blue-400 font-normal">held, not charged</span>}
                          </td>
                          <td className="px-5 py-4 text-sm">
                            {t.refundInProgress && !(t.refundedAmount > 0)
                              ? <span className="text-yellow-400">In progress</span>
                              : t.refundedAmount > 0
                                ? <span className="text-green-400" title={t.refundedAt ? `Refunded ${formatDate(t.refundedAt)}` : undefined}>−${Number(t.refundedAmount).toFixed(2)}</span>
                                : <span className="text-gray-600">—</span>}
                          </td>
                          <td className="px-5 py-4 text-sm text-white">
                            {isHeld(t.paymentStatus)
                              ? <span className="text-gray-500">—</span>
                              : t.netCharged != null ? `$${Number(t.netCharged).toFixed(2)}` : '—'}
                          </td>
                          <td className="px-5 py-4">
                            <span className={`px-2 py-1 text-xs rounded-full ${PAYMENT_STATUS_COLORS[t.paymentStatus] || 'bg-gray-600/20 text-gray-400'}`}>{PAYMENT_STATUS_LABELS[t.paymentStatus] || String(t.paymentStatus || '').replace(/_/g, ' ')}</span>
                          </td>
                          <td className="px-5 py-4 text-sm text-gray-400">{formatDate(t.hiredAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {previewPhoto && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setPreviewPhoto(null)}>
          <div className="max-w-2xl max-h-full" onClick={(e) => e.stopPropagation()}>
            <img src={previewPhoto.fileUrl} alt={previewPhoto.fileName} className="max-w-full max-h-[80vh] object-contain rounded-lg" />
            <button onClick={() => setPreviewPhoto(null)} className="mt-3 text-sm text-gray-400 hover:text-white">Close</button>
          </div>
        </div>
      )}
    </div>
  )
}

// Whether a saved address was found on the map. An address without a map point
// cannot be used to measure distances to taskers, so support can see it here.
const mapNote = (address?: string | null, lat?: number | null, lng?: number | null) => {
  if (!address) return null
  const found = typeof lat === 'number' && typeof lng === 'number'
  return found
    ? { text: '✓ Found on map', className: 'text-green-400' }
    : { text: '⚠ Not found on map — distances from this address may be unavailable', className: 'text-yellow-400' }
}

const DetailRow: React.FC<{ label: string; value: string; note?: { text: string; className: string } | null }> = ({ label, value, note }) => (
  <div className="flex justify-between py-1.5 border-b border-border-light last:border-0">
    <p className="text-xs text-gray-500">{label}</p>
    <div className="text-right max-w-xs">
      <p className="text-sm text-gray-300">{value}</p>
      {note && <p className={`text-xs mt-0.5 ${note.className}`}>{note.text}</p>}
    </div>
  </div>
)

export default CustomerDetailPage